import React, { useState } from 'react'; // Added useState
import './App.css';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom'; // Added useNavigate
import { Container, AppBar, Toolbar, Typography, Button, Box } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ReportTypeSelectionDialog from './components/ReportTypeSelectionDialog/ReportTypeSelectionDialog'; // Import the dialog
import { saveReport } from './services/firestore';
import { Timestamp } from 'firebase/firestore';
import Login from './components/Auth/Login';
import Signup from './components/Auth/Signup';
import ReportLoader from './components/Report/ReportLoader';
import ReportWizard from './components/Report/ReportWizard.jsx';
import { ReportProvider}  from './contexts/ReportContext.jsx';
import { reportConfigs } from './reports/reportConfigs';
import ProtectedRoute from './components/Layout/ProtectedRoute';
import Home from './components/Layout/Home'; // Import the new Home component
import { auth } from './services/firebase';
import { signOut } from 'firebase/auth';

// NavigationBar component
function GuestNavigation() {
  return (
    <>
      <Button color="inherit" component={Link} to="/login">Login</Button>
      <Button color="inherit" component={Link} to="/signup">Sign Up</Button>
    </>
  );
}
function UserNavigation({ handleLogout, onOpenDialog }) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const handleCreateReportClick = async () => {
    const enabledReports = Object.values(reportConfigs).filter(config => config.enabled);

    if (enabledReports.length === 1) {
      const reportConfig = enabledReports[0];
      const initialData = {
        ...reportConfig.defaultValues(),
        reportType: reportConfig.id,
        userId: currentUser.uid,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        status: 'in-progress',
        stepStatuses: {},
      };

      try {
        const reportId = await saveReport(initialData);
        const firstStepId = reportConfig.steps[0].id;
        navigate(`/${reportConfig.id}/${reportId}/${firstStepId}`);
      } catch (error) {
        console.error("Failed to create a new report:", error);
        // Handle error, maybe show a message to the user
      }
    } else {
      onOpenDialog();
    }
  };

  return (
    <>
      <Button color="inherit" component={Link} to="/">Home</Button>
      <Button color="inherit" onClick={handleCreateReportClick}>Create Report</Button>
      <Button color="inherit" onClick={handleLogout}>Logout</Button>
    </>
  );
}
function Title() {
  return (
    <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
      Economic Damages Expert Witness Report Generator
    </Typography>
  );
}
function Logo() {
  return (
    <Box
      component="img"
      sx={{
        height: 40, // Adjust height as needed
        mr: 2, // Add some margin to the right
      }}
      alt="App Logo"
      src="/logo150.png" // Path relative to public folder
    />
  );
}
const NotFoundPage = () => {
  // console.log('[App.js] NotFoundPage component rendered. Attempted Path:', window.location.pathname);
  return <Typography variant="h4">404 Not Found</Typography>;
};
function NavigationBar({ currentUser, handleLogout }) {
    const navigate = useNavigate(); // For dialog selection
    const [dialogOpen, setDialogOpen] = useState(false);

    const handleOpenDialog = () => {
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
    };

    const handleSelectReportType = (reportType) => {
        setDialogOpen(false);
        // Navigation is now handled within ReportTypeSelectionDialog
    };

    return (
        <>
            <AppBar position="static">
                <Toolbar>
                    <Logo />
                    <Title />
                    {currentUser ? (
                        <UserNavigation
                            handleLogout={handleLogout}
                            onOpenDialog={handleOpenDialog}
                        />
                    ) : (
                        <GuestNavigation />
                    )}
                </Toolbar>
            </AppBar>
            <ReportTypeSelectionDialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                onSelectReportType={handleSelectReportType}
            />
        </>
    );
}

function AppContent() {
  const { currentUser } = useAuth();

  const handleLogout = async () => {
    try {
      await signOut(auth);
      // Navigation to login will happen automatically via ProtectedRoute/redirects
    } catch (error) {
      console.error("Failed to log out", error);
      // Handle logout error (e.g., show message to user)
    }
  };

  return (
    <Router>
      <NavigationBar currentUser={currentUser} handleLogout={handleLogout} />
      <Container component="main" maxWidth="xl" sx={{ mt: 4 }}>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={currentUser ? <Navigate to="/" /> : <Login />} />
          <Route path="/signup" element={currentUser ? <Navigate to="/" /> : <Signup />} />

          {/* Protected Routes */}
          <Route
            path="/report/life-care-plan/:reportId"
            element={
              <ProtectedRoute>
                <ReportLoader />
              </ProtectedRoute>
            }
          />
           {/* Update route to accept reportId parameter */}
           <Route
            path="/view-report/:reportId"
            element={
              <ProtectedRoute>
                <ReportLoader />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:reportType/:reportId/:stepId"
            element={
              <ProtectedRoute>
                <ReportProvider>
                  <ReportWizard reportConfigs={reportConfigs} />
                </ReportProvider>
              </ProtectedRoute>
            }
          />
           {/* TODO: Add Dashboard route */}
          {/* <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard /> // Create Dashboard component
              </ProtectedRoute>
            }
          /> */}

          {/* Home Route */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Home />
              </ProtectedRoute>
            }
          />

           {/* Catch-all for undefined routes (optional) */}
           <Route path="*" element={<NotFoundPage />} />

        </Routes>
      </Container>
    </Router>
  );
}

// Wrap AppContent with AuthProvider
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
